import React from 'react';

import { useForm } from 'react-final-form';
import { SerializedEditorState } from 'lexical';

import { Box } from '@vk-hr-tek/ui/Box';
import { useInject } from '@vk-hr-tek/core/ioc';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { WysiwygInput } from '@vk-hr-tek/app-ui/form';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';
import { ValidationService } from '@vk-hr-tek/core/validation';

import { useDispatch } from '@app/hooks';
import { ContentService } from '@app/gen/content';

import { wysiwygConfig } from '../../../../constants';

export interface QuestionProps {
  isEdit: boolean;
  questionName: string;
  initialWysiwygInputValue: string | undefined;
}

export const Question = ({
  isEdit,
  questionName,
  initialWysiwygInputValue,
}: QuestionProps) => {
  const form = useForm();

  const dispatch = useDispatch();

  const contentService = useInject(ContentService);
  const validator = useInject(ValidationService);

  const handleWysiwygInputChange = (body: {
    hasContent: boolean;
    data: SerializedEditorState;
  }) => {
    form.change(`${questionName}.content`, {
      ...body,
      data: JSON.stringify(body.data),
    });
  };

  const loadFile = async (file: File, type: 'file' | 'image') => {
    const attachment = await contentService.uploadAttachment({
      body: { file, type },
    });

    form.mutators.push(`${questionName}.attachments`, attachment);

    return { name: file.name, id: attachment.id, type };
  };

  const onUploadError = (message: string, title?: string) => {
    dispatch(showCustomNotification([{ title, message, type: 'error' }]));
  };

  const getAttachmentUrl = async (id: string): Promise<string> => {
    const { url } = await contentService.getAttachmentUrl({
      params: { attachment_id: id },
    });

    return url;
  };

  return (
    <Box px="24" display="flex" flexDirection="column">
      <Box display="flex" justifyContent="space-between">
        <Typography variant="subtitle2">
          Вопрос{' '}
          <Typography component="span" color="original.brand.primary">
            *
          </Typography>
        </Typography>
      </Box>

      <WysiwygInput
        required
        showErrors
        withoutPaddings
        hideSubmitButton
        loadFile={loadFile}
        maxCharLimit={1000}
        showCharLimitCounter
        stickyToolbar={false}
        config={wysiwygConfig}
        onUploadError={onUploadError}
        name={`${questionName}.content`}
        getAttachmentUrl={getAttachmentUrl}
        onChange={handleWysiwygInputChange}
        placeholder="Введите текст вопроса"
        apiUrl={window.REACT_APP_VKHRTEK_API_URL}
        initialValue={initialWysiwygInputValue ?? '{}'}
        validateArray={
          isEdit ? undefined : [validator.requiredWysiwyg('Обязательное поле')]
        }
      />
    </Box>
  );
};
